import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { getApiUrl } from '@/lib/subdomain'
import { SteamIcon, FortniteIcon, XboxIcon } from '@/components/PlatformIcons'

const apiUrl = getApiUrl()

interface LiveActivity {
  id: string
  type: 'playing' | 'achievement' | 'online' | 'offline'
  platform: 'steam' | 'epic' | 'xbox'
  title: string
  description?: string
  timestamp: string
}

interface LiveStatus {
  isOnline: boolean
  currentActivity?: {
    platform: string
    game: string | null
    isPlaying?: boolean
  }
  recentActivities: LiveActivity[]
  connectedPlatforms?: {
    steam: boolean
    fortnite: boolean
    xbox: boolean
  }
}

const PLATFORM_ICONS: Record<string, (props: any) => JSX.Element> = {
  steam: SteamIcon,
  epic: FortniteIcon,
  xbox: XboxIcon,
}
const PLATFORM_COLORS: Record<string, string> = {
  steam: '#66c0f4',
  epic: '#9d4dbb',
  xbox: '#107c10',
}
const PLATFORM_NAMES: Record<string, string> = {
  steam: 'Steam',
  epic: 'Fortnite',
  xbox: 'Xbox',
  Steam: 'Steam',
  Fortnite: 'Fortnite',
  Xbox: 'Xbox',
}

export function LiveFeed() {
  const [activityLimit, setActivityLimit] = useState(50)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['live', 'status', activityLimit],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/live/status?limit=${activityLimit}`)
      if (!res.ok) throw new Error('Failed to fetch live status')
      return res.json() as Promise<LiveStatus>
    },
    refetchInterval: 30000,
  })

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-16">
        <div className="flex justify-center items-center min-h-[40vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-500 border-t-transparent mx-auto mb-4" />
            <p className="text-gray-400 text-sm">Checking live status...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-16 text-center">
        <h2 className="text-2xl font-display font-bold mb-2">Connection Error</h2>
        <p className="text-gray-400 mb-6">Unable to fetch live status.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
        >
          Try Again
        </button>
      </div>
    )
  }

  const isLive = data?.isOnline || false
  const activity = data?.currentActivity

  return (
    <div className="container mx-auto max-w-3xl px-4 py-8 space-y-8">
      {/* Status Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-3 mb-4">
          <div className={`w-3 h-3 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-600'}`} />
          <span className="text-lg font-semibold text-gray-300">
            {isLive ? 'Currently Online' : 'Offline'}
          </span>
        </div>
        <h1 className="text-3xl font-display font-bold mb-2">Live Activity</h1>
        <p className="text-gray-500 text-sm">Real-time gaming activity and status</p>
      </div>

      {/* Connected Platforms */}
      {data?.connectedPlatforms && (
        <div className="flex justify-center gap-3">
          {Object.entries(data.connectedPlatforms).map(([key, connected]) => {
            const platformKey = key === 'fortnite' ? 'epic' : key
            const Icon = PLATFORM_ICONS[platformKey]
            const color = PLATFORM_COLORS[platformKey]
            return (
              <div
                key={key}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  connected
                    ? 'bg-gray-800/50 border-gray-700'
                    : 'bg-gray-900/30 border-gray-800/50 opacity-40'
                }`}
              >
                {Icon && <Icon size={14} color={connected ? color : '#4b5563'} />}
                <span className="text-sm font-medium">{PLATFORM_NAMES[key] || key}</span>
                {connected && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
              </div>
            )
          })}
        </div>
      )}

      {/* Current Activity */}
      {activity && (
        <div className="bg-gray-800/50 rounded-xl border-2 border-green-500/30 p-6">
          <div className="flex items-center gap-2 text-green-400 text-sm font-medium mb-3">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            {activity.isPlaying ? 'NOW PLAYING' : 'ONLINE'}
          </div>
          {activity.game ? (
            <>
              <h2 className="text-2xl font-display font-bold mb-1">{activity.game}</h2>
              <p className="text-gray-400 text-sm">on {activity.platform}</p>
            </>
          ) : (
            <h2 className="text-xl font-display font-bold">
              Online on {activity.platform}
            </h2>
          )}
        </div>
      )}

      {/* Activity Feed */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-display font-bold">Recent Activity</h2>
          <div className="flex items-center gap-1.5">
            {[20, 50, 100].map((limit) => (
              <button
                key={limit}
                type="button"
                onClick={() => setActivityLimit(limit)}
                className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                  activityLimit === limit
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-gray-900/50 border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                {limit}
              </button>
            ))}
          </div>
        </div>
        {data?.recentActivities && data.recentActivities.length > 0 ? (
          <div className="space-y-2">
            {data.recentActivities.map((item) => (
              <ActivityRow key={item.id} activity={item} />
            ))}
          </div>
        ) : (
          <div className="bg-gray-800/50 rounded-xl border border-gray-800 p-8 text-center">
            <p className="text-gray-400">No recent activity yet</p>
            <p className="text-gray-500 text-xs mt-1">
              Activity appears here when gaming accounts are active
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

const TYPE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  playing: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Played' },
  achievement: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', label: 'Achievement' },
  online: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'Online' },
  offline: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Offline' },
}

function ActivityRow({ activity }: { activity: LiveActivity }) {
  const style = TYPE_STYLES[activity.type] || TYPE_STYLES.online
  const Icon = PLATFORM_ICONS[activity.platform]
  const color = PLATFORM_COLORS[activity.platform] || '#9ca3af'

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-gray-700 transition-colors p-4 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${style.bg}`}>
        {Icon ? <Icon size={16} color={color} /> : <span className={`text-sm ${style.text}`}>{style.label[0]}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-medium text-sm">{activity.title}</h3>
            {activity.description && (
              <p className="text-gray-500 text-xs mt-0.5 truncate">{activity.description}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            <span
              className="text-[10px] font-medium uppercase tracking-wider"
              style={{ color }}
            >
              {PLATFORM_NAMES[activity.platform] || activity.platform}
            </span>
          </div>
        </div>
        <p className="text-gray-600 text-xs mt-1">{formatTimeAgo(activity.timestamp)}</p>
      </div>
    </div>
  )
}

function formatTimeAgo(timestamp: string): string {
  const now = new Date()
  const then = new Date(timestamp)
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
  return then.toLocaleDateString()
}
